/**
 * SpacingTab — padding, margin, size, gap, and overflow controls.
 * Single responsibility: spacing and layout dimension style properties.
 */

import React from 'react';
import type { StyleProperties } from '../types';
import { BoxModelEditor } from '../shared/BoxModelEditor';
import { NumberInput } from '../shared/NumberInput';
import { SegmentedControl } from '../shared/SegmentedControl';
import { SectionLabel } from '../shared/SectionLabel';

export interface SpacingTabProps {
  value: StyleProperties;
  onChange: (p: StyleProperties) => void;
  isDark: boolean;
}

const ASPECT_RATIO_OPTIONS = [
  { value: '', label: 'Free' },
  { value: '1/1', label: '1:1' },
  { value: '4/3', label: '4:3' },
  { value: '16/9', label: '16:9' },
  { value: '21/9', label: '21:9' },
] as const;

const OBJECT_FIT_OPTIONS = [
  { value: 'fill', label: 'Fill' },
  { value: 'contain', label: 'Contain' },
  { value: 'cover', label: 'Cover' },
  { value: 'none', label: 'None' },
] as const;

const OVERFLOW_OPTIONS = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'scroll', label: 'Scroll' },
  { value: 'auto', label: 'Auto' },
] as const;

/**
 * SpacingTab — padding, margin, size constraints, gap, and overflow.
 */
export const SpacingTab: React.FC<SpacingTabProps> = ({ value, onChange, isDark }) => {
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';

  const stackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const sizeInputStyle: React.CSSProperties = {
    flex: 1,
    padding: '4px 6px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    boxSizing: 'border-box',
  };

  const sizeLabelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
    flex: 0,
    whiteSpace: 'nowrap',
    minWidth: 60,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  const SizeInput = ({
    label,
    field,
  }: {
    label: string;
    field: keyof Pick<StyleProperties, 'width' | 'height' | 'minWidth' | 'maxWidth' | 'minHeight' | 'maxHeight'>;
  }) => (
    <div style={rowStyle}>
      <span style={sizeLabelStyle}>{label}</span>
      <input
        type="text"
        value={value[field] ?? ''}
        onChange={(e) => onChange({ ...value, [field]: e.target.value || undefined })}
        placeholder="e.g. 100px, 50%, auto"
        style={sizeInputStyle}
        aria-label={label}
      />
    </div>
  );

  const MinMaxRow = ({
    minField,
    maxField,
    minLabel,
    maxLabel,
  }: {
    minField: keyof Pick<StyleProperties, 'minWidth' | 'minHeight'>;
    maxField: keyof Pick<StyleProperties, 'maxWidth' | 'maxHeight'>;
    minLabel: string;
    maxLabel: string;
  }) => (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 1 }}>
        <div style={{ ...rowStyle, justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: textSecondary, fontWeight: 600 }}>{minLabel}</span>
        </div>
        <input
          type="text"
          value={value[minField] ?? ''}
          onChange={(e) => onChange({ ...value, [minField]: e.target.value || undefined })}
          placeholder="—"
          style={{ ...sizeInputStyle, flex: 'none', width: '100%' }}
          aria-label={minLabel}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ ...rowStyle, justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: textSecondary, fontWeight: 600 }}>{maxLabel}</span>
        </div>
        <input
          type="text"
          value={value[maxField] ?? ''}
          onChange={(e) => onChange({ ...value, [maxField]: e.target.value || undefined })}
          placeholder="—"
          style={{ ...sizeInputStyle, flex: 'none', width: '100%' }}
          aria-label={maxLabel}
        />
      </div>
    </div>
  );

  return (
    <div style={stackStyle}>
      <SectionLabel label="Padding" isDark={isDark} />
      <BoxModelEditor
        label="Padding"
        top={value.paddingTop}
        right={value.paddingRight}
        bottom={value.paddingBottom}
        left={value.paddingLeft}
        onChange={(sides) =>
          onChange({
            ...value,
            paddingTop: sides.top,
            paddingRight: sides.right,
            paddingBottom: sides.bottom,
            paddingLeft: sides.left,
          })
        }
        isDark={isDark}
      />

      <SectionLabel label="Margin" isDark={isDark} />
      <BoxModelEditor
        label="Margin"
        top={value.marginTop}
        right={value.marginRight}
        bottom={value.marginBottom}
        left={value.marginLeft}
        onChange={(sides) =>
          onChange({
            ...value,
            marginTop: sides.top,
            marginRight: sides.right,
            marginBottom: sides.bottom,
            marginLeft: sides.left,
          })
        }
        isDark={isDark}
      />

      <SectionLabel label="Size" isDark={isDark} />
      <SizeInput label="Width" field="width" />
      <SizeInput label="Height" field="height" />

      <SectionLabel label="Min / Max" isDark={isDark} />
      <MinMaxRow minField="minWidth" maxField="maxWidth" minLabel="Min Width" maxLabel="Max Width" />
      <MinMaxRow minField="minHeight" maxField="maxHeight" minLabel="Min Height" maxLabel="Max Height" />

      <SectionLabel label="Aspect & Object" isDark={isDark} />
      <SegmentedControl
        label="Aspect Ratio"
        options={ASPECT_RATIO_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.aspectRatio ?? ''}
        onChange={(v) => onChange({ ...value, aspectRatio: v || undefined })}
        isDark={isDark}
      />

      <SegmentedControl
        label="Object Fit"
        options={OBJECT_FIT_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.objectFit}
        onChange={(v) => onChange({ ...value, objectFit: v as StyleProperties['objectFit'] })}
        isDark={isDark}
      />

      <SectionLabel label="Gap (flex/grid)" isDark={isDark} />
      <NumberInput
        label="Gap"
        value={value.gap}
        onChange={(v) => onChange({ ...value, gap: v })}
        unit="px"
        min={0}
        isDark={isDark}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <NumberInput
            label="Row Gap"
            value={value.rowGap}
            onChange={(v) => onChange({ ...value, rowGap: v })}
            unit="px"
            min={0}
            isDark={isDark}
          />
        </div>
        <div style={{ flex: 1 }}>
          <NumberInput
            label="Col Gap"
            value={value.columnGap}
            onChange={(v) => onChange({ ...value, columnGap: v })}
            unit="px"
            min={0}
            isDark={isDark}
          />
        </div>
      </div>

      <SectionLabel label="Overflow" isDark={isDark} />
      <SegmentedControl
        label="Overflow"
        options={OVERFLOW_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.overflow}
        onChange={(v) => onChange({ ...value, overflow: v as StyleProperties['overflow'] })}
        isDark={isDark}
      />
    </div>
  );
};

SpacingTab.displayName = 'SpacingTab';
